import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Answer } from '@/components/question/forms/shared/Answer';
import { OrderingItemCreateDTO } from '@/models/Answer';
import { Divider } from '@/components/ui/daisy';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type OrderingItemMediaProps = {
  item: OrderingItemCreateDTO;
  totalItems: number;
  isEditing: boolean;
  onStartEdit: () => void;
  onFinishEdit: () => void;
  className?: string;
  onUpdateMedia: (file: File | string, altText?: string) => void;
  onUpdateValidPositions: (validPositions: number[]) => void;
  onSwitchToText: () => void;
  onMarkIncorrect: () => void;
  onDelete: () => void;
  isLoading: boolean;
};

export default function OrderingItemMedia({
  item,
  totalItems,
  isEditing,
  onStartEdit,
  onFinishEdit,
  className,
  onUpdateMedia,
  onUpdateValidPositions,
  onSwitchToText,
  onMarkIncorrect,
  onDelete,
  isLoading,
}: OrderingItemMediaProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });
  const { t: tOrdering } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.orderingForm',
  });

  const isCorrect = item.validPositions.length > 0;

  return (
    <Answer
      isCorrect={isCorrect}
      isEditing={isEditing}
      isMedia
      className={className}
    >
      <ConditionalRenderer
        condition={isEditing}
        fallback={
          <Answer.MediaPreview
            mediaFile={item.mediaFile}
            mediaLink={item.mediaLink}
            altText={item.altText ?? undefined}
            placeholder={t('newOption')}
          />
        }
      >
        <Answer.MediaInput
          initialMediaFile={item.mediaFile}
          initialAltText={item.altText ?? ''}
          onConfirm={(file, altText) => {
            onUpdateMedia(file, altText);
            onFinishEdit();
          }}
          onCancel={onFinishEdit}
        />
      </ConditionalRenderer>
      <Answer.MoreOptions>
        <Answer.MoreOptions.Positions
          totalPositions={totalItems}
          selectedPositions={item.validPositions}
          onChange={onUpdateValidPositions}
          header={t('correctIn')}
          getPositionLabel={position =>
            tOrdering('positionLabel', {
              number: String(position).padStart(2, '0'),
            })
          }
        />
        <Divider className="my-0 py-4" />
        <Answer.MoreOptions.EditText
          onClick={onSwitchToText}
          disabled={isLimitedEdition}
        />
        <Answer.MoreOptions.EditMedia
          onClick={onStartEdit}
          disabled={isLoading}
        />
        <ConditionalRenderer condition={item.validPositions.length > 0}>
          <Answer.MoreOptions.MarkIncorrect
            onClick={onMarkIncorrect}
            disabled={isLimitedEdition || isLoading}
          />
        </ConditionalRenderer>
        <Answer.MoreOptions.Delete
          onClick={onDelete}
          disabled={isLimitedEdition || isLoading}
        />
      </Answer.MoreOptions>
    </Answer>
  );
}
